<ng-container *ngIf="viewMode === 'popup'; else regularInference">
  <button mat-raised-button color="primary" class="d-flex align-items-center gap-2" (click)="openDialog()">
    <span class="d-inline-flex align-items-center justify-content-center p-1 rounded-circle"
      style="background-color: #232459;">
      <img src="/assets/Icons/airoIcon.png" height="16px" alt="Airo" />
    </span>
    {{ buttonLabel }}
  </button>
</ng-container>

<ng-template #regularInference>
  <div>
    <div class="airo-inference-container" [class.full-view]="viewMode === 'full'"
      [class.short-view]="viewMode === 'short'" [class.table-view]="viewMode === 'table'">

      <!-- Header Bar -->
      <div *ngIf="viewMode !== 'table'" class="airo-card-header">
        <span class="airo-title d-flex align-items-center gap-2">
          <span class="d-inline-flex align-items-center justify-content-center p-1 rounded-circle"
            style="background-color: #232459;">
            <img src="/assets/Icons/airoIcon.png" height="16px" alt="Airo" />
          </span>
          {{ viewMode === 'short' ? 'Overview' : 'Summary' }}
          <span *ngIf="statusColor" [style.background-color]="statusColor"
            style="width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0;">
          </span>
        </span>

        <button *ngIf="viewMode === 'short'" mat-raised-button color="primary" [disabled]="loading"
          (click)="onAiroCueClick()">
          Airo Cue
        </button>
      </div>

      <!-- Body Content -->
      <div class="airo-card-body mt-2">
        <!-- Loading State -->
        <div *ngIf="loading" class="airo-state-box d-flex align-items-center justify-content-center gap-2 py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
          <span class="fs-13 text-secondary fw-500">Fetching inference...</span>
        </div>

        <!-- Error State -->
        <div *ngIf="!loading && error"
          class="airo-state-box alert alert-danger d-flex align-items-center justify-content-between m-0 py-2 px-3 fs-13">
          <span><i class="fa fa-exclamation-triangle me-2"></i>{{ error }}</span>
          <button class="btn btn-sm btn-outline-danger py-0 px-2" (click)="retry()">Retry</button>
        </div>

        <!-- Table View Mode -->
        <div *ngIf="!loading && !error && viewMode === 'table'" class="airo-table-content">
          <div *ngIf="tableData && tableData.length > 0" class="table-container table-responsive"
            style="max-height: calc(75vh - 120px)">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <th *ngFor="let col of (tableHeaders && tableHeaders.length > 0 ? tableHeaders : getDefaultHeaders())"
                    scope="col" style="vertical-align: middle">
                    {{ getColLabel(col) }}
                  </th>
                  <th scope="col" style="vertical-align: middle; text-align: center; width: 90px;">Pins/Assessed
                    Candidates
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of tableData; let rIdx = index">
                  <ng-container
                    *ngFor="let col of (tableHeaders && tableHeaders.length > 0 ? tableHeaders : getDefaultHeaders())">
                    <td *ngIf="!getCellSpan(rIdx, col).hide"
                      [attr.rowspan]="getCellSpan(rIdx, col).rowspan > 1 ? getCellSpan(rIdx, col).rowspan : null"
                      [class.highlighted]="isGroupableCol(col)" [class.summary-cell]="isSummaryOrLastCol(col)"
                      [matTooltip]="!isSummaryOrLastCol(col) ? getCellValue(row, col, rIdx) : null">
                      <div *ngIf="isSummaryOrLastCol(col)" class="d-flex align-items-start gap-2">
                        <span *ngIf="getColor(row)" [style.background-color]="getColor(row)"
                          style="width: 10px; height: 10px; min-width: 10px; min-height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0; margin-top: 4px;"
                          [matTooltip]="'Status: ' + (row.color || '')">
                        </span>
                        <div>{{ getCellValue(row, col, rIdx) }}</div>
                      </div>
                      <div *ngIf="!isSummaryOrLastCol(col)" class="wrap-two-lines">
                        {{ getCellValue(row, col, rIdx) }}
                      </div>
                    </td>
                  </ng-container>
                  <td style="vertical-align: middle; text-align: center;">
                    <button mat-icon-button color="primary" (click)="openEvidenceDialog(row)"
                      matTooltip="View Pins/Assessed Candidates">
                      <mat-icon style="font-size: 18px; width: 18px; height: 18px; line-height: 18px;">push_pin</mat-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination Footer -->
          <div class="d-flex align-items-center font-13 mt-2" *ngIf="totalCount > 0">
            <div class="p-1">
              <div class="d-flex flex-row align-items-center text-dark">
                <span class="p-1">Displaying </span>
                <span class="p-1" style="margin-bottom: 0 !important">
                  {{ startValue + 1 }}-{{
                  endValue > totalCount ? totalCount : endValue
                  }}
                </span>
                <span class="p-1">of {{ totalCount }}</span>
                <span class="p-1">records</span>
              </div>
            </div>
            <div class="p-1">
              <select class="form-select form-select-sm table-sort-filter" aria-label="Records per page"
                [(ngModel)]="sortRecords" (ngModelChange)="CurrentPage = 1; getRecords()">
                <option *ngFor="let option of sortOptions" [value]="option">
                  {{ option }}
                </option>
              </select>
            </div>
            <div class="p-1 ms-auto">
              <ngb-pagination [pageSize]="sortRecords" [collectionSize]="totalCount" [maxSize]="2" [rotate]="true"
                [(page)]="CurrentPage" [boundaryLinks]="true" (pageChange)="getRecords()">
              </ngb-pagination>
            </div>
          </div>

          <div *ngIf="!tableData || tableData.length === 0" class="fs-13 text-muted text-center py-4">
            No AI inference records found.
          </div>
        </div>

        <!-- Full View Mode (Tab) -->
        <div *ngIf="!loading && !error && result && viewMode === 'full'" class="airo-full-content pt-2">
          <!-- Summary Callout -->
          <div *ngIf="summaryText" class="airo-callout-box mb-3">
            <div class="airo-callout-header d-flex align-items-center mb-1">
              <i class="fa fa-file-text-o me-2 text-primary"></i>
              <span class="airo-section-label">Executive Summary</span>
            </div>
            <p class="airo-summary-paragraph m-0 fs-13">{{ summaryText }}</p>
          </div>

          <!-- Recommendations List -->
          <div *ngIf="recommendationsPoints && recommendationsPoints.length > 0" class="airo-recommendations-box">
            <div class="airo-callout-header d-flex align-items-center mb-2">
              <i class="fa fa-lightbulb-o me-2 text-amber"></i>
              <span class="airo-section-label">Actionable Recommendations</span>
            </div>
            <div class="airo-recs-grid">
              <div *ngFor="let rec of recommendationsPoints; let idx = index"
                class="airo-rec-card d-flex align-items-start gap-2 mb-2">
                <span class="airo-rec-number me-2">{{ idx + 1 }}</span>
                <div class="airo-rec-text fs-13">{{ rec }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Short View Mode (Header Box) -->
        <div *ngIf="!loading && !error && result && viewMode === 'short'" class="airo-short-content pt-1">
          <div *ngIf="summaryPoints && summaryPoints.length > 0" class="airo-short-list">
            <div *ngFor="let point of summaryPoints" class="airo-short-item d-flex align-items-start gap-2 mb-2">
              <span class="airo-short-dot me-2"></span>
              <span class="airo-short-text fs-13">{{ point }}</span>
            </div>
          </div>

          <div *ngIf="(!summaryPoints || summaryPoints.length === 0)" class="fs-13 text-secondary italic p-2">
            {{ summaryText || 'Insufficient detail to generate meaningful inference for Airo Cue.' }}
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="!loading && !error && !result && viewMode !== 'table'" class="airo-empty-box text-center py-4">
          <p class="fs-13 text-muted mb-0">No AI inference found.</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Evidence Dialog Template -->
  <ng-template #evidenceDialog let-dialogRef="dialogRef">
    <div class="modal-content" style="border: none; overflow: hidden;">
      <app-modal-header-common [title]="'Pins/Assessed Candidates'" [dialogRef]="dialogRef">
      </app-modal-header-common>

      <!-- Dialog Body Content -->
      <div class="modal-body p-3" [style.max-height]="previewFile ? '80vh' : '75vh'"
        [style.overflow]="previewFile ? 'hidden' : 'auto'">
        <!-- Loading State -->
        <div *ngIf="evidenceLoading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
          <span class="fs-13 text-secondary">Fetching evidence...</span>
        </div>

        <!-- Error State -->
        <div *ngIf="!evidenceLoading && evidenceError" class="alert alert-danger fs-13 py-2 px-3 m-0">
          <i class="fa fa-exclamation-triangle me-2"></i>{{ evidenceError }}
        </div>

        <!-- Evidence Categories Content with Split Preview -->
        <div *ngIf="!evidenceLoading && !evidenceError && evidenceData" class="row m-0">
          <!-- Left Pane: Categories List -->
          <div [class.col-12]="!previewFile" [class.col-md-5]="previewFile" class="pe-md-3"
            [style.max-height]="previewFile ? 'calc(80vh - 45px)' : 'none'"
            [style.overflow-y]="previewFile ? 'auto' : 'visible'">
            <div *ngIf="evidenceData.inference_name" class="p-2 mb-2 bg-light rounded border border-light">
              <span class="fw-600 fs-13 text-dark me-1 text-capitalize">{{ evidenceData.inference_type }}:</span>
              <span class="fs-13 text-secondary">{{ evidenceData.inference_name }}</span>
            </div>

            <!-- Executive Summary Callout Box -->
            <div *ngIf="getEvidenceSummaryText()" class="airo-callout-box mb-3">
              <div class="airo-callout-header d-flex align-items-center mb-1">
                <i class="fa fa-file-text-o me-2 text-primary"></i>
                <span class="airo-section-label">Executive Summary</span>
              </div>
              <p class="airo-summary-paragraph m-0 fs-13">{{ getEvidenceSummaryText() }}</p>
            </div>

            <div *ngFor="let cat of evidenceData.categories; let last = last" class="mb-4" [class.mb-0]="last">
              <!-- Category Heading -->
              <div class="border-bottom pb-1 mb-2">
                <h6 class="fs-14 fw-700 text-dark m-0">{{ cat.category }}</h6>
              </div>

              <!-- Items List -->
              <div *ngIf="cat.items && cat.items.length > 0">
                <ul class="ps-3 m-0" *ngIf="cat.category !== 'Files'">
                  <li *ngFor="let item of cat.items" class="fs-13 text-secondary mb-2">
                    <ng-container *ngIf="isString(item)">{{ item }}</ng-container>
                    <ng-container *ngIf="!isString(item)">{{ item.name || item.title || (item | json) }}</ng-container>
                  </li>
                </ul>

                <!-- Files Category Cards -->
                <div *ngIf="cat.category === 'Files'" class="d-flex flex-column gap-2">
                  <div *ngFor="let item of cat.items"
                    class="d-flex align-items-center justify-content-between gap-2 p-2 bg-light rounded border">
                    <div class="d-flex align-items-center gap-2 text-truncate" style="cursor: pointer;"
                      (click)="previewSelectedFile(item)">
                      <mat-icon class="text-danger fs-16" *ngIf="isPdf(item)"
                        style="font-size:16px;width:16px;height:16px;line-height:16px;">picture_as_pdf</mat-icon>
                      <mat-icon class="text-success fs-16" *ngIf="isImage(item)"
                        style="font-size:16px;width:16px;height:16px;line-height:16px;">image</mat-icon>
                      <mat-icon class="text-primary fs-16" *ngIf="!isPdf(item) && !isImage(item)"
                        style="font-size:16px;width:16px;height:16px;line-height:16px;">insert_drive_file</mat-icon>
                      <span class="fw-500 text-dark fs-12 text-truncate" [matTooltip]="item.file_name">{{ item.file_name
                        }}</span>
                    </div>
                    <div class="d-flex align-items-center flex-shrink-0">
                      <button mat-icon-button color="primary" (click)="previewSelectedFile(item)" matTooltip="View">
                        <mat-icon>visibility</mat-icon>
                      </button>
                      <button mat-icon-button color="primary" (click)="downloadFile(item)" matTooltip="Download">
                        <mat-icon>download</mat-icon>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty Category Message -->
              <div *ngIf="!cat.items || cat.items.length === 0" class="fs-13 text-muted italic ps-2">
                No {{ cat.category?.toLowerCase() || 'items' }} found.
              </div>
            </div>
          </div>

          <!-- Right Pane: Embedded File Preview -->
          <div *ngIf="previewFile" class="col-md-7 pl-md-3 border-start d-flex flex-column"
            style="height: calc(80vh - 45px); overflow: hidden;">
            <div class="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom flex-shrink-0">
              <span class="fw-600 fs-13 text-dark text-truncate me-2">{{ previewFile.file_name }}</span>
              <div class="d-flex align-items-center">
                <button mat-icon-button color="primary" (click)="downloadFile(previewFile)" matTooltip="Download">
                  <mat-icon>download</mat-icon>
                </button>
                <button mat-icon-button (click)="closeFilePreview()" matTooltip="Close preview">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>

            <div class="file-preview-body p-2 d-flex align-items-center justify-content-center flex-grow-1"
              style="height: calc(80vh - 100px); background: #f8fafc; border-radius: 6px; overflow: hidden;">
              <!-- Loading PDF Spinner -->
              <div *ngIf="isLoadingPdf" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                <span class="fs-13 text-secondary">Loading PDF document...</span>
              </div>

              <!-- PDF Viewer -->
              <pdf-viewer *ngIf="!isLoadingPdf && isPdf(previewFile) && pdfData"
                [src]="{ data: pdfData, isEvalSupported: false }" [render-text]="true" [original-size]="false"
                [fit-to-page]="true" class="preview-pdf"
                style="width: 100%; height: 100%; display: block; overflow: hidden;"></pdf-viewer>

              <!-- Image Viewer -->
              <img *ngIf="!isLoadingPdf && isImage(previewFile) && previewFile.url" [src]="previewFile.url"
                alt="Preview" style="max-width: 100%; max-height: calc(80vh - 110px); object-fit: contain;" />

              <!-- Fallback / Unsupported / Error State -->
              <div *ngIf="!isLoadingPdf && !pdfData && !isImage(previewFile)" class="text-center text-muted p-4">
                <i class="fa fa-file-text-o fa-2x mb-2 text-secondary"></i>
                <div class="fs-13 fw-500 mb-2">Preview not available for this document.</div>
                <button class="btn btn-sm btn-primary" (click)="downloadFile(previewFile)">
                  <i class="fa fa-download me-1"></i> Download File
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- No Evidence Data Found -->
        <div
          *ngIf="!evidenceLoading && !evidenceError && (!evidenceData || !evidenceData.categories || evidenceData.categories.length === 0)"
          class="text-center py-4 text-muted fs-13">
          <i class="fa fa-info-circle me-1"></i> No evidence records found.
        </div>
      </div>
    </div>
  </ng-template>
</ng-template>

<!-- Internal Dialog Template for Button View Mode -->
<ng-template #dialogTemplate let-dialogRef="dialogRef">
  <div class="modal-content" style="border: none">
    <app-modal-header-common [title]="buttonLabel" [dialogRef]="dialogRef">
    </app-modal-header-common>
    <div class="modal-body p-3" style="max-height: calc(85vh - 60px); overflow: hidden;">
      <app-llm-inference [inferenceType]="inferenceType" viewMode="table" [viewContext]="viewContext || 'popup'"
        [outputRef]="outputRef" [promptVars]="promptVars" [customFilterMap]="customFilterMap">
      </app-llm-inference>
    </div>
  </div>
</ng-template>